import { authUtils } from '../utils/authUtils';
import { Result } from '../models/result';

class HttpRepository {
  constructor() {
    this.baseHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /** [customeToken] is optional. If provided, it will be used instead of the current token. */
  getAuthHeaders(options = {}) {
    try {
        const {customToken, isSecretSecured, isPublicAPI} = options;

        if(isPublicAPI){
          return new Result({
            value: {
              ...this.baseHeaders
            }
          });
        }
      const token = customToken??authUtils.getCurrentToken();

      if(isSecretSecured){
        return new Result({
          value: {
            ...this.baseHeaders
          }
        });
      }
      if (!token) {
        return new Result({
          message: 'No authentication token available',
          code: 'AUTH_TOKEN_MISSING'
        });
      }
      
      return new Result({
        value: {
            ...this.baseHeaders,
            'Authorization': `Bearer ${token}`
          }
      });
    } catch (error) {
      console.error('Error getting auth headers:', error);
      return new Result({
        message: 'Authentication failed',
        code: 'AUTH_ERROR'
      });
    }
  }

  async handleAuthError(response, url, options, responseType = 'json') {
    try {
      let responseData;
      
     
        responseData = await response.json();
      
      
      if (response.status === 401 || response.status === 403) {
        const isTokenError = responseData?.code === 'AUTH_TOKEN_INVALID' || 
                           responseData?.message?.toLowerCase().includes('token') ||
                           responseData?.message?.toLowerCase().includes('auth');
        
        if (isTokenError) {
          console.log('Token appears to be invalid, refreshing...');
          
          const refreshResult = await authUtils.refreshToken();
          if (!refreshResult.success) {
            return new Result({
              message: 'Authentication failed - token refresh unsuccessful',
              code: 'AUTH_REFRESH_FAILED'
            });
          }
          
          const freshHeaders = this.getAuthHeaders();
          if (!freshHeaders.success) {
            return freshHeaders;
          }
          
          const retryOptions = {
            ...options,
            headers: {
              ...options.headers,
              ...freshHeaders.value
            }
          };
          
          const retryResponse = await fetch(url, retryOptions);
          
          if (retryResponse.status === 401 || retryResponse.status === 403) {
            return new Result({
              message: 'Authentication failed even after token refresh',
              code: 'AUTH_RETRY_FAILED'
            });
          }
          
          // Handle retry response based on response type
          if (responseType === 'blob') {
            const retryData = await retryResponse.blob();
            return new Result({ value: retryData });
          } else if (responseType === 'stream') {
            return new Result({ value: retryResponse });
          } else {
            const retryData = await retryResponse.json();
            return retryData;
          }
        }
      }
      
      // Return the response data as a Result object
      return responseData;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('JSON')) {
        return new Result({
          message: `HTTP ${response.status}: ${response.statusText}`,
          code: `HTTP_${response.status}`
        });
      }
      return new Result({
        message: error.message,
        code: 'PARSE_ERROR'
      });
    }
  }

  async get(url, options = {}) {
    try {
      const customToken = options?.userToken??null;
      const headers = this.getAuthHeaders({customToken: customToken, isSecretSecured: options?.isSecretSecured??false,
        isPublicAPI: options?.isPublicAPI??false
      });
      
      // Check if headers is a Result object (error case)
      if (!headers.success) {
        return headers;
      }
      
      const fetchOptions = {
        method: 'GET',
        headers: {
          ...headers.value,
          ...options.headers
        },
        ...options
      };

      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        return await this.handleAuthError(response, url, fetchOptions, 'json');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('GET request failed:', error);
      return new Result({
        message: `GET request failed: ${error.message}`,
        code: 'NETWORK_ERROR'
      });
    }
  }

  async post(url, data = null, options = {}) {
    try {
        const customToken = options?.userToken??null;
      const headers = this.getAuthHeaders({customToken: customToken, isSecretSecured: options?.isSecretSecured??false,
        isPublicAPI: options?.isPublicAPI??false
      });
      
      // Check if headers is a Result object (error case)
      if (!headers.success) {
        return headers;
      }
      
      const fetchOptions = {
        method: 'POST',
        headers: {
          ...headers.value,
          ...options.headers
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        return await this.handleAuthError(response, url, fetchOptions, 'json');
      }
      
      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('POST request failed:', error);
      return new Result({
        message: `POST request failed: ${error.message}`,
        code: 'NETWORK_ERROR'
      });
    }
  }

  async postBlob(url, data = null, options = {}) {
    try {
      const headers = this.getAuthHeaders({
        isPublicAPI: options?.isPublicAPI??false
      });
      
      // Check if headers is a Result object (error case)
      if (!headers.success) {
        return headers;
      }
      
      const fetchOptions = {
        method: 'POST',
        headers: {
          ...headers.value,
          ...options.headers
        },
        body: data ? JSON.stringify(data) : undefined,
      };

      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        return await this.handleAuthError(response, url, fetchOptions, 'blob');
      }
      
      // For blob responses, return the blob directly
      const blob = await response.blob();
      return new Result({ value: blob });
    } catch (error) {
      console.error('POST blob request failed:', error);
      return new Result({
        message: `POST blob request failed: ${error.message}`,
        code: 'NETWORK_ERROR'
      });
    }
  }

  async postStream(url, data = null, options = {}) {
    try {
      const headers = this.getAuthHeaders();
      
      // Check if headers is a Result object (error case)
      if (!headers.success) {
        return headers;
      }
      
      const fetchOptions = {
        method: 'POST',
        headers: {
          ...headers.value,
          'Accept': 'text/event-stream',
          ...options.headers
        },
        body: data ? JSON.stringify(data) : undefined
      };


      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        return await this.handleAuthError(response, url, fetchOptions, 'stream');
      }
      
      // For streaming, return the response wrapped in a Result
      return new Result({ value: response });
    } catch (error) {
      console.error('Streaming request failed:', error);
      return new Result({
        message: `Streaming request failed: ${error.message}`,
        code: 'NETWORK_ERROR'
      });
    }
  }
}

// Export singleton instance
const httpRepo = new HttpRepository();
export default httpRepo;
